import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/contexts/LanguageContext";
import { toast } from "sonner";
import { Trash2, Pencil, ArrowRight } from "lucide-react";

export default function History() {
  const { authAxios } = useAuth();
  const { tr } = useLang();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await authAxios.get("/conversations");
      setItems(r.data);
    } catch (e) {}
    setLoading(false);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const rename = async (id) => {
    const title = window.prompt(tr("hist_rename_prompt"));
    if (!title) return;
    try {
      await authAxios.patch(`/conversations/${id}`, { title });
      toast.success("Updated");
      load();
    } catch (e) { toast.error("Error"); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this conversation?")) return;
    try {
      await authAxios.delete(`/conversations/${id}`);
      toast.success("Deleted");
      load();
    } catch (e) { toast.error("Error"); }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-1 max-w-5xl mx-auto w-full px-4 sm:px-6 lg:px-10 py-10">
        <div className="pfi-overline">PERSONA AI PERFUMER</div>
        <h1 className="mt-3 font-serif text-3xl sm:text-4xl">{tr("hist_title")}</h1>
        <p className="mt-2 text-[#a9a9b8] text-sm">{tr("hist_sub")}</p>

        <div className="mt-8">
          {loading ? (
            <div className="text-[#a9a9b8] text-sm">{tr("loading")}</div>
          ) : items.length === 0 ? (
            <div className="pfi-card p-10 text-center text-[#a9a9b8]">
              {tr("hist_empty")}
              <div className="mt-4">
                <Link to="/ai-perfumer" className="pfi-btn-gold rounded-full px-5 py-2.5 text-sm inline-flex items-center gap-2">
                  {tr("ai_new_conversation")} <ArrowRight size={14} />
                </Link>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              {items.map((c) => (
                <div key={c.conversation_id} data-testid={`hist-item-${c.conversation_id}`} className="pfi-card p-5 flex items-center justify-between gap-4">
                  <div className="min-w-0">
                    <div className="pfi-overline">{new Date(c.updated_at).toLocaleString()}</div>
                    <div className="mt-1 font-serif text-lg truncate">{c.title || "Untitled"}</div>
                    <div className="text-xs text-[#6c6c7a] mt-1">{c.message_count} messages</div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Link data-testid={`open-${c.conversation_id}`} to={`/ai-perfumer/${c.conversation_id}`} className="pfi-btn-outline rounded-full px-3 py-2 text-xs">
                      {tr("hist_open")}
                    </Link>
                    <button data-testid={`rename-${c.conversation_id}`} onClick={() => rename(c.conversation_id)} className="pfi-btn-outline rounded-full h-9 w-9 inline-flex items-center justify-center" aria-label="Rename">
                      <Pencil size={14} />
                    </button>
                    <button data-testid={`delete-${c.conversation_id}`} onClick={() => del(c.conversation_id)} className="pfi-btn-outline rounded-full h-9 w-9 inline-flex items-center justify-center text-red-300 border-red-300/30 hover:bg-red-500/10" aria-label="Delete">
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
